import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type {
  MaterialFormValues,
  MaterialGroup,
  EquipmentSuggestion,
} from "../types";
import type { UseFormReturn } from "react-hook-form";

type MaterialFormProps = {
  open: boolean;
  mode: "create" | "edit";
  simpleMode: boolean;
  materialGroups: MaterialGroup[];
  isLoadingGroups: boolean;
  isSubmitting: boolean;
  form: UseFormReturn<MaterialFormValues>;
  descriptionFieldRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  suggestions: EquipmentSuggestion[];
  isSearching: boolean;
  fetchSuggestions: (query: string) => void;
  onSuggestionSelect: (suggestion: EquipmentSuggestion) => void;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onCancel: () => void;
  onClose: () => void;
};

export const MaterialForm: React.FC<MaterialFormProps> = ({
  open,
  mode,
  simpleMode,
  materialGroups,
  isLoadingGroups,
  isSubmitting,
  form,
  descriptionFieldRef,
  suggestions,
  isSearching,
  fetchSuggestions,
  onSuggestionSelect,
  onSubmit,
  onCancel,
  onClose,
}) => {
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? onClose() : null)}>
      <DialogContent className="max-h-[90dvh] w-[95vw] max-w-3xl overflow-y-auto p-0">
        <DialogHeader className="px-6 pb-2 pt-6">
          <DialogTitle>{mode === "edit" ? "Editar material" : "Novo material"}</DialogTitle>
          <DialogDescription>
            Preencha os detalhes do material. Campos adicionais estão disponíveis
            no modo completo.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6 px-6 pb-6" onSubmit={onSubmit}>
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="identifier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cod.</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Cod." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem className="relative">
                    <FormLabel>Modelo</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Modelo"
                        autoComplete="off"
                        onChange={(event) => {
                          field.onChange(event);
                          fetchSuggestions(event.target.value);
                        }}
                      />
                    </FormControl>
                    {(suggestions.length > 0 || isSearching) && (
                      <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-md border bg-popover shadow-md">
                        <ul>
                          {isSearching ? (
                            <li className="px-3 py-2 text-sm text-muted-foreground">
                              A procurar…
                            </li>
                          ) : (
                            suggestions.map((suggestion) => {
                              const label = `${suggestion.brand ? `${suggestion.brand} ` : ""}${suggestion.model}`.trim();
                              return (
                                <li key={`${suggestion.id}-${suggestion.model}`}>
                                  <button
                                    type="button"
                                    className="flex w-full items-center justify-start px-3 py-2 text-sm hover:bg-muted"
                                    onClick={() => onSuggestionSelect(suggestion)}
                                  >
                                    {label}
                                  </button>
                                </li>
                              );
                            })
                          )}
                        </ul>
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Marca</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Marca" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      ref={(node) => {
                        field.ref(node);
                        descriptionFieldRef.current = node;
                      }}
                      rows={3}
                      placeholder="Descrição"
                      className="min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!simpleMode ? (
              <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="newStock"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número de Peças Novas</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            {...field}
                            value={Number.isNaN(field.value) ? "" : field.value}
                            onChange={(event) => field.onChange(event.target.value)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="usedStock"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número de Peças Usadas</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            {...field}
                            value={Number.isNaN(field.value) ? "" : field.value}
                            onChange={(event) => field.onChange(event.target.value)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="stockMin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Stock Mínimo</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            {...field}
                            value={Number.isNaN(field.value) ? "" : field.value}
                            onChange={(event) => field.onChange(event.target.value)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="storeBox"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-1">
                        <FormLabel>Caixa</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Caixa" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-1">
                        <FormLabel>Preço de Compra</FormLabel>
                        <FormControl>
                          <div className="flex items-stretch overflow-hidden rounded-md border bg-background">
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              className="border-none"
                              {...field}
                              value={Number.isNaN(field.value) ? "" : field.value}
                              onChange={(event) => field.onChange(event.target.value)}
                              placeholder="Preço"
                            />
                            <span className="flex items-center bg-muted px-3 text-sm">€</span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="materialGroup"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Categoria</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            className={cn(
                              "h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              field.value ? "text-foreground" : "text-muted-foreground",
                            )}
                          >
                            <option value="">Selecione uma categoria</option>
                            {materialGroups.map((group) => (
                              <option key={group.id} value={group.id}>
                                {group.name}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        {isLoadingGroups ? (
                          <p className="text-xs text-muted-foreground">
                            A carregar categorias…
                          </p>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            ) : null}

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "A guardar…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
